import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ServerInterfaceNetwork, ServerInterfaceNetworkForm} from "../../../../../../../../services/model/server.model";
import {ServerService} from "../../../../../../../../services/back/server.service";
import {CurrentTenantService} from "../../../../../../../../services/front/current-tenant.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {Observable, of, switchMap, tap} from "rxjs";
import {Network, NetworkService} from "../../../../../../../../services/back/network.service";
import {NgForOf, NgIf} from "@angular/common";
import {DesignSystemModule} from "../../../../../../../design-system/design-system.module";
import {map} from "rxjs/operators";
import {ipAddressValidator} from "../../../../../../../../utils/forms.utils";
import {IpCheckerService} from "../../../../../../../../services/front/ip-checker.service";

@Component({
  selector: 'app-server-interface-form-dialog',
	imports: [
		NgIf,
		TranslateModule,
		DesignSystemModule,
		NgForOf
	],
  templateUrl: './server-interface-form-dialog.component.html',
  styleUrl: './server-interface-form-dialog.component.scss'
})
export class ServerInterfaceFormDialogComponent implements OnInit {
	tenantId: string;

	_saving: boolean;
	initialized: boolean = false;

	serverInterfaceForm: FormGroup;

	networkList: Network[] = [];

	form: typeof Form = Form;

	constructor(@Inject(MAT_DIALOG_DATA) public data: ServerInterfaceFormDialogData,
				public dialog: MatDialogRef<ServerInterfaceFormDialogComponent>,
				public serverService: ServerService,
				public currentTenantService: CurrentTenantService,
				public networkService: NetworkService,
				public translate: TranslateService,
				public ipCheckerService: IpCheckerService) {
	}

	ngOnInit(): void {
		this.buildForm();
		this.setDefaultData();
		this.currentTenantService.getCurrentTenantId()
			.pipe(tap(tenantId => this.tenantId = tenantId))
			.subscribe(() => this.initialize());
	}

	private buildForm() {
		this.serverInterfaceForm = new FormGroup({
			[Form.IP_ADDRESS]: new FormControl(undefined, [Validators.required, ipAddressValidator(), this.duplicateIpValidator()]),
			[Form.NETWORK]: new FormControl(undefined),
			[Form.IS_PRIMARY]: new FormControl(false, Validators.required)
		});
	}

	isIpInNetworkRange(): boolean {
		if (this.serverInterfaceForm.get(Form.NETWORK)?.value && this.serverInterfaceForm.get(Form.IP_ADDRESS)?.value)
			return this.ipCheckerService.isIpInRange(this.serverInterfaceForm.get(Form.IP_ADDRESS)!.value, this.serverInterfaceForm.get(Form.NETWORK)!.value!.ipAddress, this.serverInterfaceForm.get(Form.NETWORK)!.value!.subnet);
		else
			return true;
	}

	initialize(): void {
		this.networkService.getTenantNetworks(this.tenantId)
			.pipe(
				tap(networks => this.buildSelectionLists(networks)),
				tap(() => this.setDefaultData())
			).subscribe(() => this.initialized = true);
	}

	private buildSelectionLists(networks: Network[]) {
		this.networkList = networks;
	}

	private setDefaultData() {
		if (this.data.serverInterface) {
			this.serverInterfaceForm.get(Form.IP_ADDRESS)!.setValue(this.data.serverInterface.serverInterface.ipAddress)
			this.serverInterfaceForm.get(Form.IS_PRIMARY)!.setValue(this.data.serverInterface.serverInterface.isPrimary)
			this.serverInterfaceForm.get(Form.NETWORK)!.setValue(this.data.serverInterface.network)
		} else {
			this.serverInterfaceForm.get(Form.IS_PRIMARY)!.setValue(false)
			if (this.data.alreadyExistingIpAddresses.length <= 0) {
				this.serverInterfaceForm.get(Form.IS_PRIMARY)!.setValue(true)
			}
		}
	}

	save(): void {
		this.switchSaving()
			.pipe(
				map(() => this.buildServerInterfaceForm()),
				switchMap(form => this.serverService.createOrUpdateServerInterface(this.tenantId, this.data.serverId, form)),
				switchMap(() => this.switchSaving()))
			.subscribe(() => this.close(true))
	}

	buildServerInterfaceForm(): ServerInterfaceNetworkForm {
		return {
			previousIpAddress: this.data.serverInterface?.serverInterface.ipAddress || this.serverInterfaceForm.get(Form.IP_ADDRESS)!.value,
			ipAddress: this.serverInterfaceForm.get(Form.IP_ADDRESS)!.value,
			isPrimary: this.serverInterfaceForm.get(Form.IS_PRIMARY)!.value,
			networkId: this.serverInterfaceForm.get(Form.NETWORK)!.value?.id || null,
		}
	}

	close(event: boolean): void {
		this.dialog.close(event);
	}

	switchSaving(): Observable<{}> {
		this._saving = !this._saving;
		return of({});
	}

	private duplicateIpValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!control.value || !this.data.alreadyExistingIpAddresses || this.data.alreadyExistingIpAddresses.length === 0) {
				return null;
			}

			if (this.data.serverInterface && control.value === this.data.serverInterface.serverInterface.ipAddress) {
				return null;
			}

			const isDuplicate = this.data.alreadyExistingIpAddresses.includes(control.value);

			return isDuplicate ? { duplicateIp: true } : null;
		};
	}
}

export interface ServerInterfaceFormDialogData {
	serverId: string;
	serverInterface?: ServerInterfaceNetwork;
	alreadyExistingIpAddresses: string[];
}

enum Form {
	IP_ADDRESS = "ipAddress",
	NETWORK = "network",
	IS_PRIMARY = "isPrimary"
}
